import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { Body1, Caption } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import {
  approvalOutlineSelectSx,
  ApprovalTableRowDeleteButton,
} from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildGroupSalesMultiContentHtml,
  GroupSalesMultiPassportView,
  GroupSalesMultiSnapshot,
} from '@/components/Approval/@components/GroupSales';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import { getGroupSalesMultiDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import useGroupSalesShopOptions from '@/hooks/approval/useGroupSalesShopOptions';
import useFileUpload from '@/hooks/approval/useFileUpload';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import FileUploadIcon from '@/public/icons/approval/file-upload.svg';
import { getNestedDataFetcher } from '@/utils/apis';
import { fetchDownloadUrl } from '@/utils/approval/fetchDownloadUrl';
import type { GroupSalesCandidate } from '@/utils/approval/groupSales';
import {
  buildGroupSalesMultiGroup,
  GROUP_SALES_MULTI_MAX_ITEMS,
  GROUP_SALES_MULTI_SAME_TYPE_MESSAGE,
  groupSalesMultiCandidateKey,
  groupSalesMultiItems,
  groupSalesMultiTagNumber,
  updateGroupSalesMultiPassport,
  type GroupSalesMultiDetail,
  type GroupSalesMultiDraftSource,
  type GroupSalesMultiGroup,
  type GroupSalesMultiItem,
} from '@/utils/approval/groupSalesMulti';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const DATE_FORMAT = 'YYYY-MM-DD';
const CONTROL_HEIGHT = 44;

const toParam = (date: Dayjs | null) =>
  date?.isValid() ? date.format(DATE_FORMAT) : undefined;

/** 레거시 kendoMultiSelect group(SHORTNAME) — 매장 이름순으로 묶음 */
const buildCandidateOptions = (
  candidates: GroupSalesCandidate[],
): SearchableSelectOption[] =>
  [...candidates]
    .sort((a, b) => (a.shopName ?? '').localeCompare(b.shopName ?? '', 'ko'))
    .map((c) => ({
      value: groupSalesMultiCandidateKey(c),
      label: c.label ?? '',
      group: c.shopName ?? '',
    }));

type PassportEditorProps = {
  item: GroupSalesMultiItem;
  onChange: (
    passport: Pick<GroupSalesMultiItem, 'passport' | 'passport_no'>,
  ) => void;
};

/** 레거시 여권 칸 — 없으면 올리기, 있으면 보기·삭제 */
const PassportEditor = ({ item, onChange }: PassportEditorProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useFileUpload('default');

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const result = await upload(file);
      const url = await fetchDownloadUrl(result.uuid)
        .then((res) => res.url)
        .catch(() => '');
      onChange({ passport: url, passport_no: result.uuid });
    } catch {
      snackbar.showError({ message: t('업로드에 실패했습니다.') });
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (isUploading)
    return (
      <PassportBox>
        <Spinner theme="black" size="16px" />
      </PassportBox>
    );
  if (item.passport || item.passport_no)
    return (
      <PassportBox>
        <GroupSalesMultiPassportView
          url={item.passport}
          uuid={item.passport_no}
        />
        <ApprovalTableRowDeleteButton
          type="button"
          aria-label={t('삭제')}
          title={t('삭제')}
          onClick={() =>
            dialog.show({
              message: t('등록한 여권사진을 삭제하시겠습니까?'),
              confirmButtonText: t('삭제'),
              cancelButtonText: t('취소'),
              onConfirm: () => onChange({ passport: '', passport_no: '' }),
            })
          }
        />
      </PassportBox>
    );
  return (
    <>
      <IconButton
        size="small"
        aria-label={t('여권 등록')}
        title={t('여권 등록')}
        onClick={() => inputRef.current?.click()}
      >
        <FileUploadIcon />
      </IconButton>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/*,.pdf"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </>
  );
};

type Props = { isEditMode: boolean };

/**
 * 단체판매 진행(다중)(131) — 브랜드·매장·기간으로 조회 → 여러 건 선택 → 「확인」으로 매장별 내역 확정.
 * 조건·선택을 바꿔도 확정한 표·저장값은 「확인」을 다시 누를 때까지 유지(레거시), 「확인」이 실패하면 비움. 편집은 레거시처럼 건을 바꿀 수 없고 내역을 다시 불러와 저장 HTML 갱신
 */
const GroupSalesMultiField = ({ isEditMode }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue, getValues } = useFormContext<ApprovalFormValues>();
  const confirmed = useWatch({ control, name: 'groupSalesMulti' });
  const { options: brandOptions } = useMyBrandOptions('A');

  const [brandCode, setBrandCode] = useState('');
  const [shopCode, setShopCode] = useState('');
  const { options: shopOptions } = useGroupSalesShopOptions(
    isEditMode ? '' : brandCode,
    getGroupSalesMultiDraftSourceUrl,
  );
  const [fromDate, setFromDate] = useState<Dayjs | null>(() =>
    dayjs().startOf('month'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(() => dayjs());
  const [candidates, setCandidates] = useState<GroupSalesCandidate[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [details, setDetails] = useState<GroupSalesMultiDetail[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  // 다시 조회하면 늦게 온 이전 조회 응답이 후보 목록을 덮지 않게 무효 처리
  const searchIdRef = useRef(0);
  const confirmIdRef = useRef(0);

  // 레거시: 조회는 고른 건만 비우고, 확정한 표·저장값은 「확인」을 다시 누를 때까지 그대로
  const resetCandidates = () => {
    searchIdRef.current += 1;
    setIsSearching(false);
    setCandidates([]);
    setSelectedKeys([]);
  };

  useEffect(() => {
    if (isEditMode || brandCode || !brandOptions.length) return;
    setBrandCode(brandOptions[0].value);
  }, [isEditMode, brandCode, brandOptions]);

  // 레거시: 매장 목록이 오면 첫 매장을 골라 바로 조회
  useEffect(() => {
    if (isEditMode || !shopOptions.length) return;
    const first = shopOptions[0].value;
    setShopCode(first);
    search('1', { shopCode: first });
  }, [isEditMode, shopOptions]);

  const search = async (
    searchType: '1' | '2',
    override?: { shopCode?: string },
  ) => {
    if (!brandCode) return;
    resetCandidates();
    const requestId = searchIdRef.current;
    setIsSearching(true);
    try {
      const data = await getNestedDataFetcher<GroupSalesMultiDraftSource>(
        getGroupSalesMultiDraftSourceUrl({
          brandCode,
          searchType,
          ...(searchType === '1'
            ? {
                shopCode: override?.shopCode ?? shopCode,
                fromDate: toParam(fromDate),
                toDate: toParam(toDate),
              }
            : {}),
        }),
      );
      if (requestId !== searchIdRef.current) return;
      // 구분·날짜를 문구에서 읽어 문구 없는 후보는 고를 수 없음(레거시는 고르면 스크립트 오류)
      setCandidates(
        (data.display.candidates ?? []).filter(
          (c) => c.sequence && c.shopCode && c.label,
        ),
      );
    } catch {
      if (requestId !== searchIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === searchIdRef.current) setIsSearching(false);
    }
  };

  const fetchDetails = async (group: GroupSalesMultiGroup) => {
    const data = await getNestedDataFetcher<GroupSalesMultiDraftSource>(
      getGroupSalesMultiDraftSourceUrl({
        brandCode: group.brand,
        saleType: group.type,
        items: groupSalesMultiItems(group),
      }),
    );
    return data.display.items ?? [];
  };

  const handleConfirm = async () => {
    if (isConfirming) return;
    const selected = selectedKeys
      .map((key) =>
        candidates.find((c) => groupSalesMultiCandidateKey(c) === key),
      )
      .filter((c): c is GroupSalesCandidate => !!c);
    const built = buildGroupSalesMultiGroup(selected);
    if ('error' in built) {
      snackbar.showError({
        message: t(built.error, { max: GROUP_SALES_MULTI_MAX_ITEMS }),
      });
      return;
    }
    const { group } = built;
    confirmIdRef.current += 1;
    const requestId = confirmIdRef.current;
    setIsConfirming(true);
    try {
      const loaded = await fetchDetails(group);
      if (requestId !== confirmIdRef.current) return;
      setDetails(loaded);
      const [first] = group.shops;
      const others = group.shops.length - 1;
      setValue(
        'subject',
        others > 0
          ? t('단체판매 진행의 건 - {{brand}} {{shop}} 외 {{count}}', {
              brand: group.name_eng,
              shop: first.shortname,
              count: others,
            })
          : t('단체판매 진행의 건 - {{brand}} {{shop}}', {
              brand: group.name_eng,
              shop: first.shortname,
            }),
        { shouldDirty: true },
      );
      setValue(
        'groupSalesMulti',
        { group, content: buildGroupSalesMultiContentHtml(group, loaded, t) },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== confirmIdRef.current) return;
      // 안내를 놓쳐도 이전 건으로 상신되지 않게 비움 — 저장·상신 검사에서 막힘
      setDetails(null);
      setValue('groupSalesMulti', null, { shouldDirty: true });
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === confirmIdRef.current) setIsConfirming(false);
    }
  };

  // 편집: 저장된 건들의 지금 내역으로 표·저장 HTML 갱신(여권은 저장값 유지). 실패하면 저장 표를 그대로 보여줌
  const editTargetKey =
    isEditMode && confirmed
      ? JSON.stringify(groupSalesMultiItems(confirmed.group))
      : null;
  useEffect(() => {
    const saved = getValues('groupSalesMulti');
    if (!isEditMode || !saved) return;
    let cancelled = false;
    fetchDetails(saved.group)
      .then((loaded) => {
        if (cancelled) return;
        const current = getValues('groupSalesMulti') ?? saved;
        setDetails(loaded);
        setValue('groupSalesMulti', {
          ...current,
          content: buildGroupSalesMultiContentHtml(current.group, loaded, t),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [editTargetKey]);

  const changePassport = (
    shopIndex: number,
    itemIndex: number,
    passport: Pick<GroupSalesMultiItem, 'passport' | 'passport_no'>,
  ) => {
    const current = getValues('groupSalesMulti');
    if (!current || !details) return;
    const group = updateGroupSalesMultiPassport(
      current.group,
      shopIndex,
      itemIndex,
      passport,
    );
    setValue(
      'groupSalesMulti',
      { group, content: buildGroupSalesMultiContentHtml(group, details, t) },
      { shouldDirty: true },
    );
  };

  const renderTable = () => {
    if (!confirmed?.content) return null;
    // 내역을 다시 못 불러오면 저장 HTML 을 고칠 수 없어 여권도 읽기 전용
    return (
      <GroupSalesMultiSnapshot
        content={confirmed.content}
        renderPassport={
          details
            ? (s, i) => {
                const item = confirmed.group.shops[s]?.items[i];
                return item ? (
                  <PassportEditor
                    item={item}
                    onChange={(passport) => changePassport(s, i, passport)}
                  />
                ) : null;
              }
            : undefined
        }
      />
    );
  };

  if (isEditMode) return <FieldBlock>{renderTable()}</FieldBlock>;

  const candidateByKey = new Map(
    candidates.map((c) => [groupSalesMultiCandidateKey(c), c]),
  );
  return (
    <FieldBlock>
      <FieldHeader>
        <Body1>{t('단체판매')}</Body1>
      </FieldHeader>
      <ControlRow>
        <Select
          value={brandCode}
          options={brandOptions}
          placeholder={t('브랜드 선택')}
          onChange={(e) => {
            resetCandidates();
            setShopCode('');
            setBrandCode(String(e.target.value));
          }}
          sx={{ ...approvalOutlineSelectSx, width: 180 }}
        />
        <Select
          value={shopCode}
          options={shopOptions}
          placeholder={t('매장 선택')}
          onChange={(e) => setShopCode(String(e.target.value))}
          sx={{ ...approvalOutlineSelectSx, width: 200 }}
        />
        <DateRange>
          <DatePicker
            value={fromDate}
            onChange={setFromDate}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
          <span>~</span>
          <DatePicker
            value={toDate}
            onChange={setToDate}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
        </DateRange>
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={!brandCode || isSearching}
          startIcon={
            isSearching ? <Spinner theme="black" size="16px" /> : undefined
          }
          onClick={() => search('1')}
        >
          {t('조회')}
        </BoxButton>
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={!brandCode || isSearching}
          onClick={() => search('2')}
        >
          {t('전체조회')}
        </BoxButton>
      </ControlRow>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedKeys}
          options={buildCandidateOptions(candidates)}
          placeholder={t('단체판매 건을 조회해 주세요.')}
          noOptionsText={t('단체판매 건을 조회해 주세요.')}
          renderValue={(keys) =>
            keys
              .map((key) => {
                const c = candidateByKey.get(key);
                return c
                  ? `${c.shopName ?? ''} ${groupSalesMultiTagNumber(c)}`
                  : '';
              })
              .join(', ')
          }
          onChange={setSelectedKeys}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={isConfirming}
          startIcon={
            isConfirming ? <Spinner theme="white" size="16px" /> : undefined
          }
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      <Caption sx={{ mb: 1, color: 'error.main' }}>
        {`* ${t(GROUP_SALES_MULTI_SAME_TYPE_MESSAGE)}`}
      </Caption>
      {renderTable()}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const DateRange = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const PassportBox = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
});

export default GroupSalesMultiField;
